import React from 'react';
import { Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { LocalShipping, Settings } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { formatCurrency } from 'utils';
import { NO_DATA } from 'constants/common';
import { calcDiscount } from 'modules/equipment/lib/charges';

import { EquipmentDetailsEquipment } from './EquipmentDetailsTypes';

interface EquipmentInfoTabProps {
  equipment: EquipmentDetailsEquipment | undefined;
  specs: Record<string, unknown> | null;
}

export const EquipmentInfoTab = ({ equipment, specs }: EquipmentInfoTabProps) => {
  const basicInfoRows: Array<{ label: string; value: string }> = [
    { label: 'Stock Number', value: equipment?.stockId || NO_DATA },
    { label: 'VIN/Serial Number', value: equipment?.serialNumberVin || NO_DATA },
    { label: 'Year', value: equipment?.year || NO_DATA },
    { label: 'Condition', value: equipment?.inventoryItem?.condition || equipment?.newUsed || NO_DATA },
    { label: 'Make', value: equipment?.make || NO_DATA },
    { label: 'Model', value: equipment?.model || NO_DATA },
    { label: 'Mileage', value: equipment?.mileage != null ? String(equipment.mileage) : NO_DATA },
    { label: 'Class', value: equipment?.classType || NO_DATA },
    { label: 'Equipment Type', value: equipment?.assetTypeStr || NO_DATA },
    { label: 'Status', value: equipment?.inventoryItem?.status || NO_DATA },
    { label: 'Location', value: NO_DATA },
  ];

  const priceRows: Array<{ label: string; value: string }> = [
    {
      label: 'Cost',
      value: equipment?.wholesaleCost != null ? formatCurrency(equipment.wholesaleCost, true) : NO_DATA,
    },
    {
      label: 'Retail',
      value: equipment?.retailPrice != null ? formatCurrency(equipment.retailPrice, true) : NO_DATA,
    },
    {
      label: 'Final Customer Price',
      value:
        equipment?.retailPrice != null && equipment?.discountType
          ? formatCurrency(
              calcDiscount(equipment.retailPrice, equipment.discountValue ?? 0, equipment.discountType as any),
              true
            )
          : NO_DATA,
    },
  ];

  return (
    <Stack direction="row" gap={3} mt={2} flexWrap="wrap">
      {/* Left Column: Basic Info */}
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <LocalShipping fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
            Basic Info
          </Typography>
        </Stack>
        <Table size="small">
          <TableBody>
            {basicInfoRows.map(row => (
              <TableRow key={row.label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                  {row.label}
                </TableCell>
                <TableCell sx={{ border: 'none', py: 0.75 }}>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Right Column: Price + Additional Info */}
      <Stack sx={{ flex: 1, minWidth: 0 }} gap={3}>
        {/* Price */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
              $ Price
            </Typography>
          </Stack>
          <Table size="small">
            <TableBody>
              {priceRows.map(row => (
                <TableRow key={row.label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                    {row.label}
                  </TableCell>
                  <TableCell sx={{ border: 'none', py: 0.75 }}>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* Additional Info */}
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <Settings fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
              Additional Info
            </Typography>
          </Stack>
          {specs && Object.keys(specs).length > 0 ? (
            <Table size="small">
              <TableBody>
                {Object.entries(specs).map(([key, value]) => (
                  <TableRow key={key} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ color: grey[600], border: 'none', pl: 0, py: 0.75, width: '45%' }}>
                      {key}
                    </TableCell>
                    <TableCell sx={{ border: 'none', py: 0.75 }}>{value != null ? String(value) : NO_DATA}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No additional info available.
            </Typography>
          )}
        </Paper>
      </Stack>
    </Stack>
  );
};
